export type ForeignNationalDocumentFilingStatus =
  | "pending"
  | "reviewed"
  | "flagged";

/**
 * Cached text of a vault document. Filled the first time the document is
 * read on demand (natively, or by Extend for scans); absent until then.
 */
export type ForeignNationalDocumentOcr = {
  provider?: string | null;
  text?: string | null;
  text_storage_path?: string | null;
  page_count?: number | null;
  warnings?: string[] | null;
};

export type ForeignNationalDocumentCaseUsage = {
  case_id: string;
  individual_id: string;
  added_at?: unknown;
  added_by_user_id?: string | null;
  /** The case file the send created. */
  node_id?: string | null;
};

export type ForeignNationalDocument = {
  foreign_national_document_id: string;
  foreign_national_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  storage_path: string;
  download_url: string;
  /** What the document is, when known (e.g. the intake question it answered). */
  label?: string | null;
  filing_status: ForeignNationalDocumentFilingStatus;
  created_by_user_id?: string | null;
  created_by_user_email?: string | null;
  created_by_user_name?: string | null;
  created_by_user_avatar_url?: string | null;
  created_at?: unknown;
  updated_at?: unknown;
  is_deleted?: boolean;
  ocr?: ForeignNationalDocumentOcr | null;
  used_in_cases?: ForeignNationalDocumentCaseUsage[] | null;
  /** Visible in the client portal (a client always sees their own uploads). */
  shared_with_client?: boolean;
  /** The case file this was saved from. */
  source_case_node_id?: string | null;
  /** sha256 of the current bytes; an editor's save sends it back as its base. */
  checksum?: string | null;
  /** Earlier bytes, oldest first (firm only; the portal never sees them). */
  versions?: ForeignNationalDocumentVersion[] | null;
};

/** Bytes an editor saved over, or a restore brought another version back over. */
export type ForeignNationalDocumentVersion = {
  storage_path: string;
  filename: string;
  content_type: string;
  size_bytes?: number | null;
  checksum?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  /** The edit that replaced these bytes. */
  note?: string | null;
};

/** Largest file a client's documents accept (the API refuses anything larger). */
export const MAX_CLIENT_DOCUMENT_BYTES = 200 * 1024 * 1024;

/** The types a client's documents keep, by file extension: the case-file types. */
export const CLIENT_DOCUMENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation"
};

/** `accept` for a client-document file picker. */
export const CLIENT_DOCUMENT_ACCEPT = Object.keys(CLIENT_DOCUMENT_TYPES)
  .map((ext) => `.${ext}`)
  .join(",");

/**
 * Opened by `POST .../documents/uploads` (or `.../versions/uploads` for an
 * editor's save); the browser PUTs to `upload_url` with `content_type`.
 */
export type ClientDocumentUploadSession = {
  upload_id: string;
  upload_url: string;
  content_type?: string;
};

export type CaseDocumentKind = "identity" | "status" | "education" | "work";

export type CaseDocumentState = "current" | "ended" | "expired" | "historical";

/** A file on one of the client's cases (passport, I-94, diploma, CV). */
export type ClientCaseDocument = {
  id: string;
  node_id: string;
  individual_id: string;
  title: string;
  /** "Passport", "I-94", "I-797A", "CV"… */
  label: string;
  document_type?: string | null;
  kind: CaseDocumentKind;
  mime_type?: string | null;
  size_bytes?: number | null;
  /** Opens the file in the Case IDE. */
  path: string;
  cases: Array<{
    individual_id: string;
    case_id?: string | null;
    case_label: string;
    node_id: string;
    path: string;
  }>;
  facts: {
    status?: string | null;
    passport_number?: string | null;
    nationality?: string | null;
    valid_from?: string | null;
    expires?: string | null;
    duration_of_status?: boolean;
    receipt_number?: string | null;
    i94_number?: string | null;
  };
  state: CaseDocumentState | null;
  /** Already saved to the client's documents. */
  client_document_id: string | null;
  uploaded_at?: string | null;
};

/** One case a checklist document is filed on. */
export type ChecklistCaseUse = {
  case_id: string;
  individual_id?: string | null;
  /** Tells the client's cases apart: "H-1B", "H-1B 2023". */
  case_label: string;
  node_id?: string | null;
  /** Opens the case copy in the Case IDE, when known. */
  path?: string | null;
};

export type ChecklistAddedBy =
  | { kind: "client"; name: string | null }
  | { kind: "firm"; name: string | null }
  /** A case file whose uploader isn't known: "Added to H-1B 2023". */
  | { kind: "case"; name: null; case_label?: string | null };

export type ChecklistExpiryTone = "ok" | "soon" | "expired";

/**
 * One document on the checklist: a client document (`source: "client"`,
 * with its case copies merged in) or a case file nobody saved to the client
 * (`source: "case"`).
 */
export type ChecklistEntry = {
  id: string;
  /** The client document; null for a case-only file. */
  document_id: string | null;
  source: "client" | "case";
  /** The slot key ("passport", "w2:2025"); null in Other. */
  slot: string | null;
  display_name: string | null;
  title: string | null;
  filename: string | null;
  label?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  download_url?: string | null;
  added_at?: string | null;
  added_by: ChecklistAddedBy;
  facts: Record<string, unknown>;
  /** What the key facts read off the file: "P8841207 · India". */
  summary: string | null;
  expires: string | null;
  expiry_tone: ChecklistExpiryTone | null;
  state: "current" | "expired" | "superseded";
  /** `new`: the client's upload, not reviewed yet. */
  review: "new" | "flagged" | null;
  filing_status: ForeignNationalDocumentFilingStatus | null;
  client_upload: boolean;
  shared_with_client: boolean;
  cases: ChecklistCaseUse[];
  /** The case file this is (or was copied to). */
  node: { individual_id: string; node_id: string; path?: string | null } | null;
  signature: { id: string; status: string; title?: string | null } | null;
};

export type ChecklistSlotStatus = "on_file" | "missing" | "requested";

export type ChecklistSlot = {
  key: string;
  name: string;
  kind: CaseDocumentKind;
  /** On the expected list for the client's cases. */
  expected: boolean;
  status: ChecklistSlotStatus;
  /** Current documents: one for passports and notices, every one for W-2s and diplomas. */
  entries: ChecklistEntry[];
  /** Earlier copies of a single-instance slot, newest first. */
  older: ChecklistEntry[];
  missing_note: string | null;
  /**
   * Why the slot is expected: the visa family's list, the profile (the
   * degree on the education), or an open notice that asks for it.
   */
  source?: ChecklistSlotSource | null;
  /** The "Expected because…" note, on file or not. */
  why?: string | null;
  request: { intake_id: string; title: string; sent_at: string | null } | null;
  attention: boolean;
};

export type ChecklistSlotSource = "visa_family" | "profile" | "notice";

/** An employer document a notice asks for (MSA, SOW): the company's to provide. */
export type ChecklistCompanyRequest = {
  name: string;
  issue: string;
  notice_label: string;
  individual_id: string;
  company_id?: string | null;
  company_name?: string | null;
};

export type ChecklistInternalKind =
  | "draft"
  | "form"
  | "packet"
  | "plan"
  | "file";

/**
 * Something the firm made or keeps on a case (a draft, filled form, packet,
 * plan, or a case file marked Internal / Work Product), read where it lives.
 */
export type ChecklistInternalItem = {
  id: string;
  kind: ChecklistInternalKind;
  ref_id: string;
  individual_id: string;
  title: string;
  /** "Draft · Exhibit List", "Case File · Marked Internal". */
  subtitle: string;
  updated_at?: string | null;
  /** Opens the item in the Case IDE. */
  path: string;
  mime_type?: string | null;
};

export type ChecklistInternalCase = {
  individual_id: string;
  case_id: string | null;
  case_label: string;
  items: ChecklistInternalItem[];
};

export type ChecklistSection = {
  kind: CaseDocumentKind;
  title: string;
  on_file: number;
  expected: number;
  slots: ChecklistSlot[];
};

export type ChecklistTypeOption = {
  key: string;
  name: string;
  kind: CaseDocumentKind;
};

export type ChecklistResponse = {
  foreign_national_id: string;
  client_name: string | null;
  sections: ChecklistSection[];
  /** Client documents whose type isn't set. */
  other: ChecklistEntry[];
  needs_attention: number;
  company_requests?: ChecklistCompanyRequest[];
  /** The firm's own files, by case (attorney-only; the portal never gets them). */
  internal?: ChecklistInternalCase[];
  set_type_options: ChecklistTypeOption[];
  today: string;
};
